import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** Pure helpers for the bill processing queue: what an operator sees, and in what order. */

export const WORK_STATES = {
  Unclaimed: 'Unclaimed',
  Working: 'Working',
  ReadyForQa: 'Ready for QA',
  NoBillAvailable: 'No bill available',
  InvalidCredentials: 'Invalid credentials',
  Blocked: 'Blocked',
  ErrorWithAllocation: 'Error with allocation',
  SkippedBiMonthly: 'Skipped, bi-monthly',
  SkippedQuarterly: 'Skipped, quarterly',
  DoNotBill: 'Do not bill',
  SkippedOther: 'Skipped, other'
};

/**
 * States that end this row's cycle. Recording one drops the row out of the default view and
 * releases the claim, because the BA is done with it whether or not a bill exists.
 *
 * The two cadence skips are separate from "no bill available" on purpose: a bi-monthly utility has
 * no bill in half its cycles by design, and recording that as unavailable makes it look late and
 * corrupts the publication-lag prediction.
 */
export const DEAD_END_STATES = ['NoBillAvailable', 'SkippedBiMonthly', 'SkippedQuarterly', 'InvalidCredentials', 'Blocked', 'SkippedOther', 'DoNotBill'];
export const WORK_STATE_COLORS = {
  Unclaimed: 'default',
  Working: 'info',
  ReadyForQa: 'success',
  NoBillAvailable: 'warning',
  InvalidCredentials: 'error',
  Blocked: 'error',
  ErrorWithAllocation: 'error',
  SkippedBiMonthly: 'default',
  SkippedQuarterly: 'default',
  DoNotBill: 'default',
  SkippedOther: 'default'
};
export const workStateLabel = value => WORK_STATES[value] || value || WORK_STATES.Unclaimed;

/**
 * Why a row still has no bill (property-service BillQueueReasons). "Waiting" rows have a working login and a bill not
 * due yet: the daily collection is expected to fill them, so the default view leaves them out.
 */
export const QUEUE_REASONS = {
  manual: {
    label: 'manual',
    color: 'text.secondary'
  },
  automation_failed: {
    label: 'automation failed',
    color: 'error.main'
  },
  automated_waiting: {
    label: 'automated, waiting',
    color: 'info.main'
  },
  no_portal: {
    label: 'no portal',
    color: 'text.secondary'
  }
};
export const queueReason = row => QUEUE_REASONS[row === null || row === void 0 ? void 0 : row.reason] || (row !== null && row !== void 0 && row.isAutomated ? QUEUE_REASONS.automated_waiting : QUEUE_REASONS.manual);
export const formatCycle = (year, month) => year && month ? new Date(year, month - 1, 1).toLocaleDateString(undefined, {
  month: 'short',
  year: 'numeric'
}) : '';

/** `YYYY-MM-DD` as UTC ms. `new Date(value)` reads back a day early west of Greenwich. */
const parseCalendarDate = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value !== null && value !== void 0 ? value : ''));
  if (!match) return null;
  const _match = _slicedToArray(match, 4),
    year = _match[1],
    month = _match[2],
    day = _match[3];
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
};

/** Days overdue; negative means not yet expected. Null with no history, which is not zero. */
export const overdueDays = function (item) {
  let today = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const expected = parseCalendarDate(item === null || item === void 0 ? void 0 : item.expectedBillDate);
  if (expected === null) return null;
  return Math.round((Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - expected) / 86400000);
};
export const dueLabel = function (item) {
  let today = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const days = overdueDays(item, today);
  if (days === null) return 'No history';
  if (days === 0) return 'Due today';
  if (days > 0) return "".concat(days, " day").concat(days === 1 ? '' : 's', " overdue");
  return "In ".concat(Math.abs(days), " day").concat(Math.abs(days) === 1 ? '' : 's');
};
const toIsoDate = utcMs => new Date(utcMs).toISOString().slice(0, 10);

/**
 * The likely service period, prefilled so the operator corrects rather than types.
 *
 * Starts the day after the last bill ended: periods abut, and an overlap double-bills a day. Empty
 * with no history rather than guessing a month that reads authoritative.
 */
export const suggestedPeriod = item => {
  const lastEnd = parseCalendarDate(item === null || item === void 0 ? void 0 : item.lastBillEnd);
  if (lastEnd === null) return {
    startDate: '',
    endDate: ''
  };
  const start = lastEnd + 86400000;
  const cadence = Number(item === null || item === void 0 ? void 0 : item.cadenceDays) > 0 ? Number(item.cadenceDays) : 30;
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(start + (cadence - 1) * 86400000)
  };
};

/** Why this bill cannot be submitted, or null. Mirrors the server, which re-checks. */
/** Matches the server's guard. A mistyped year would otherwise own this property's prediction. */
const MAX_BILL_DATE_LAG_DAYS = 180;
const DAY_MS = 86400000;
export const rejectBillEntry = function () {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    startDate = _ref.startDate,
    endDate = _ref.endDate,
    amount = _ref.amount,
    billDate = _ref.billDate;
  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);
  if (start === null || end === null) return 'Service start and end are both required.';
  if (end < start) return 'The service period ends before it starts.';
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 'A bill amount is required.';

  // Optional, so an absent one is fine; a present one has to be plausible.
  if (billDate) {
    const billed = parseCalendarDate(billDate);
    if (billed === null) return 'That bill date is not a date.';
    if (billed < start) return 'The bill date is before the service period starts.';
    if ((billed - end) / DAY_MS > MAX_BILL_DATE_LAG_DAYS) {
      return "The bill date is more than ".concat(MAX_BILL_DATE_LAG_DAYS, " days after the service period ends.");
    }
  }
  return null;
};

/**
 * What actually gets POSTed for a bill entry.
 *
 * `fileId` is omitted rather than sent empty: the API binds it to `Guid?`, and `""` is not a Guid,
 * so an empty string is a 400 on a form the operator has already filled in correctly.
 */
export const billEntryPayload = function () {
  let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    startDate = _ref2.startDate,
    endDate = _ref2.endDate,
    amount = _ref2.amount,
    billDate = _ref2.billDate;
  let fileId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return _objectSpread(_objectSpread({
    startDate,
    endDate,
    amount: Number(amount)
  }, fileId ? {
    fileId
  } : {}), billDate ? {
    billDate
  } : {});
};

/**
 * Why a row is expected when it is, in words a BA can challenge.
 *
 * A bare date invites "the queue is wrong". Naming the sample size and where it came from lets an
 * operator who knows the provider moved its billing day say so.
 */
export const lagExplanation = item => {
  if (!item) return '';
  // The day itself is not on the row, and a 29-31 override clamps in short months, so the date would misstate it.
  if (item.lagBasis === 'override') return 'Set by hand on the utility to a day of the month.';
  const lag = item.publicationLagDays;
  if (lag === null || lag === undefined || item.lagBasis === 'none') {
    return 'No bill-date history yet, so this is the end of the service period.';
  }
  const days = lag === 1 ? '1 day' : "".concat(lag, " days");
  if (item.lagBasis === 'provider') {
    return "Usually arrives ".concat(days, " after the period ends, from this provider's other properties.");
  }
  const samples = item.lagSampleCount === 1 ? '1 bill' : "".concat(item.lagSampleCount, " bills");
  return "Usually arrives ".concat(days, " after the period ends, from ").concat(samples, ".");
};

/** The four view filters are exclusive: picking one clears the others, so two never narrow each other silently. */
export const VIEW_FILTERS = ['needsPerson', 'onlyManual', 'automationFailed', 'late'];
export const withView = (filters, view, checked) => _objectSpread(_objectSpread(_objectSpread({}, filters), Object.fromEntries(VIEW_FILTERS.map(key => [key, false]))), {}, {
  [view]: checked
});

/**
 * The query string for the queue read. Falsy filters are dropped by the API client, so only switched-on
 * filters travel (`late=true`, never `late=false`).
 */
export const queueQuery = function () {
  let filters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let search = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return {
    cycleId: filters.cycleId || undefined,
    needsPerson: filters.needsPerson,
    onlyManual: filters.onlyManual,
    onlyUnclaimed: filters.onlyUnclaimed,
    automationFailed: filters.automationFailed,
    late: filters.late,
    mine: filters.mine,
    workState: filters.workState || undefined,
    includeDeadEnds: filters.includeDeadEnds || undefined,
    search: search || undefined,
    take: 500
  };
};

/** Deep link into the allocation modal. Company included: the modal is company-scoped. */
export const applyBillingLink = item => {
  if (!(item !== null && item !== void 0 && item.propertyId)) return null;
  const params = new URLSearchParams({
    propertyId: item.propertyId,
    utilityId: item.utilityId || ''
  });
  if (item.companyId) params.set('companyId', item.companyId);
  if (item.billingCycleId) params.set('billingCycleId', item.billingCycleId);
  return "/billing?".concat(params.toString());
};

/** Columns that must never be exported: a CSV with credentials in it is the tracker again. */
export const CREDENTIAL_FIELDS = ['usernameHint', 'credentialStatus', 'credentialId'];

/** Filtered at the export call, so marking a credential column exportable cannot leak it. */
export const exportableColumns = columns => (columns || []).filter(column => !CREDENTIAL_FIELDS.includes(column.field));

/** Next in the order the operator is looking at, not some server-side ordering they cannot see. */
export const nextItem = (items, currentId) => {
  const list = Array.isArray(items) ? items : [];
  const index = list.findIndex(i => i.allocationTableId === currentId);
  if (index < 0) return list[0] || null;
  return list[index + 1] || null;
};
export const previousItem = (items, currentId) => {
  const list = Array.isArray(items) ? items : [];
  const index = list.findIndex(i => i.allocationTableId === currentId);
  if (index <= 0) return null;
  return list[index - 1];
};

/** Tabs: AP Push holds both AP Push lanes; Bill Automation holds Bill Automation alone. */
export const LANE_TABS = [{
  value: 'ApPush',
  label: 'AP Push'
}, {
  value: 'BillAutomation',
  label: 'Bill Automation'
}, {
  value: 'Changes',
  label: 'Changes'
}];
export const LANES = {
  ApPushAndBillAutomation: {
    label: 'Urgent: AP Push + Bill Automation',
    color: 'error'
  },
  ApPush: {
    label: 'AP Push',
    color: 'info'
  },
  BillAutomation: {
    label: 'Bill Automation',
    color: 'primary'
  },
  NotEnrolled: {
    label: 'Not turned on',
    color: 'default'
  }
};
export const STAGES = {
  ReadyToAllocate: 'Ready to allocate',
  WaitingForBill: 'Waiting for bill',
  ReadyToApprove: 'Ready to approve'
};
export const STATUSES = {
  LoginFailing: {
    label: 'Login failing',
    color: 'error'
  },
  Late: {
    label: 'Late',
    color: 'error'
  },
  Due: {
    label: 'Due',
    color: 'warning'
  },
  NotDue: {
    label: 'Not due yet',
    color: 'default'
  },
  NoHistory: {
    label: 'No bill history',
    color: 'default'
  },
  ReceivedEmail: {
    label: 'Received by email',
    color: 'success'
  },
  ReceivedUpload: {
    label: 'Received by upload',
    color: 'success'
  },
  ReceivedPortal: {
    label: 'Received from portal',
    color: 'success'
  },
  ReceivedIntegration: {
    label: 'Received by integration',
    color: 'success'
  },
  WaitingForQa: {
    label: 'Waiting for QA',
    color: 'info'
  },
  SentBack: {
    label: 'Sent back by QA',
    color: 'warning'
  }
};
export const statusOf = value => STATUSES[value] || {
  label: value || '-',
  color: 'default'
};
export const laneOf = value => LANES[value] || LANES.NotEnrolled;

/** How a received bill arrived, in words, from bill-automation's BillSource name. */
export const SOURCE_LABELS = {
  Email: 'email',
  Scrape: 'portal login',
  // bill-automation's Portal source is the bulk upload screen in our portal, not a utility portal login.
  Portal: 'bulk upload',
  Manual: 'upload',
  Api: 'upload',
  Integration: 'integration'
};

/** Grid column to the API's sortBy. A cleared sort goes back to the work order. */
export const SORT_FIELDS = {
  propertyAddress: 'address',
  companyName: 'company',
  providerName: 'provider',
  utilityTypeName: 'utility',
  status: 'status',
  stage: 'stage',
  expectedBillDate: 'expected',
  daysToCutOff: 'cutoff',
  receivedOn: 'received'
};
export const sortParams = sortModel => {
  const _ref3 = sortModel || [],
    _ref4 = _slicedToArray(_ref3, 1),
    first = _ref4[0];
  return first && SORT_FIELDS[first.field] ? {
    sortBy: SORT_FIELDS[first.field],
    sortDir: first.sort || 'asc'
  } : {};
};

/** The v2 query: a lane is always sent, which is what tells the API to answer with lanes, stages and facets. */
export const laneQuery = function () {
  let view = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return _objectSpread(_objectSpread({
    lane: view.lane || 'ApPush',
    includeNotEnrolled: view.includeNotEnrolled || undefined,
    stage: view.stage || undefined,
    status: view.status || undefined,
    companyId: view.companyId || undefined,
    providerName: view.providerName || undefined,
    utilityType: view.utilityType || undefined,
    search: view.search || undefined,
    includeDeadEnds: view.includeDeadEnds || undefined
  }, sortParams(view.sortModel)), {}, {
    skip: (view.page || 0) * (view.pageSize || 100),
    take: view.pageSize || 100
  });
};

/** Tab counts from the lane facet: AP Push is both AP Push lanes. */
export const tabCounts = function () {
  let lanes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    ApPush: (lanes.ApPushAndBillAutomation || 0) + (lanes.ApPush || 0),
    BillAutomation: lanes.BillAutomation || 0,
    urgent: lanes.ApPushAndBillAutomation || 0
  };
};

/** Percent change from the bill before, or null for the oldest one shown. */
export const changeFromPrevious = (bills, index) => {
  var _bills$index, _bills;
  const current = bills === null || bills === void 0 || (_bills$index = bills[index]) === null || _bills$index === void 0 ? void 0 : _bills$index.amount;
  const previous = bills === null || bills === void 0 || (_bills = bills[index + 1]) === null || _bills === void 0 ? void 0 : _bills.amount;
  if (current == null || !previous) return null;
  return Math.round((current - previous) / previous * 1000) / 10;
};